import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { RequestMethod } from '@api/abstracts';
import { MockData } from '@api/mock';
import { LogsData } from '@shared/services/logs';
import { Observable } from 'rxjs';

/**
 * HTTP interceptor providing mock data responses for development and testing.
 *
 * @remarks
 * - Only active when `MockData.isActivated` is `true`
 * - Falls back to real HTTP requests when no mock is found
 * - Matches requests by HTTP method and URL with parameters
 *
 * @example
 * ```
 * // In app.config.ts
 * provideHttpClient(withInterceptorsFromDi());
 *
 * // Usage in MockData service
 * getMockResponse(method: string, url: string): Observable<HttpEvent<unknown>> | null {
 *   if (method === 'GET' && url.includes('/api/users')) {
 *     return of(new HttpResponse({ body: [{ id: '1', name: 'Mock User' }] }));
 *   }
 *   return null;
 * }
 * ```
 */
@Injectable()
export class MockInterceptor implements HttpInterceptor {
  private readonly mockData = inject(MockData);
  private readonly logs = inject(LogsData);

  /**
   * Intercepts HTTP requests to provide mock responses when enabled.
   *
   * @param req - Outgoing HTTP request
   * @param next - Next handler in the interceptor chain
   * @returns Observable of HTTP event (mock or real response)
   */
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.mockData.isActivated) {
      const method: keyof typeof RequestMethod = req.method as keyof typeof RequestMethod;
      const response = this.mockData.getMockResponse(method, req.urlWithParams);
      if (response) {
        this.logs.debug(`%c[MOCK] ${req.method} ${req.url}`, 'color: #868e96');
        return response;
      }

      return next.handle(req);
    } else {
      return next.handle(req);
    }
  }
}
